import { AddPublicationButton } from '../../../addPublicationButton/AddPublicationButton';
import { isPastCalendarDay } from '../functions';
import { VISIBLE_PUBS_PER_CELL } from '../calendarCellAnchor';
import { InlinePublicationCard } from '../InlinePublicationCard';
import { PublicationPill } from '../PublicationPill';
import { MoreCellPublicationsButton } from '../MoreCellPublicationsButton';
import { CalendarGranularity, CalendarPublication, CalendarPublicationHoverAnchor } from '../types';

const CELL_BASE_CLASS =
    'group relative flex h-[150px] flex-col border-b border-r border-light-gray p-1';
const DAY_HEADER_CLASS = 'mb-1 flex shrink-0 items-start justify-between';
const DAY_NUMBER_CLASS = 'text-more-small font-medium';
const ADD_BUTTON_HEADER_CLASS =
    'pointer-events-none shrink-0 opacity-0 transition-opacity group-hover:pointer-events-auto group-hover:opacity-100';
const ADD_BUTTON_OVERLAY_EMPTY_CLASS =
    'pointer-events-none absolute inset-0 z-10 flex items-center justify-center opacity-0 transition-opacity group-hover:pointer-events-auto group-hover:opacity-100';

type MonthDayCellProps = Readonly<{
    day: Date;
    dayKey: string;
    dayNumberLabel: string;
    publications: readonly CalendarPublication[];
    inMonth: boolean;
    isLastCol: boolean;
    isLastRow: boolean;
    onPublicationHoverStart: (
        publication: CalendarPublication,
        anchor: CalendarPublicationHoverAnchor,
    ) => void;
    onPublicationHoverEnd: () => void;
    onOpenDayList: (day: Date, top: number, left: number) => void;
}>;

export function MonthDayCell({
    day,
    dayKey,
    dayNumberLabel,
    publications,
    inMonth,
    isLastCol,
    isLastRow,
    onPublicationHoverStart,
    onPublicationHoverEnd,
    onOpenDayList,
}: MonthDayCellProps) {
    const showCreateButton = inMonth && !isPastCalendarDay(day);
    const hasPublications = publications.length > 0;
    const isSinglePublication = publications.length === 1;
    const visible = publications.slice(0, VISIBLE_PUBS_PER_CELL);
    const extra = publications.length - VISIBLE_PUBS_PER_CELL;
    const presetSchedule = { date: dayKey, hour: '09', minute: '00' } as const;

    const cellClass = [
        CELL_BASE_CLASS,
        isLastCol ? 'border-r-0' : '',
        isLastRow ? 'border-b-0' : '',
        inMonth ? 'bg-white' : 'bg-primary-light/60',
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <div data-calendar-day-cell className={cellClass}>
            <div className={DAY_HEADER_CLASS}>
                <span className={DAY_NUMBER_CLASS}>{dayNumberLabel}</span>
                {hasPublications ? (
                    <div
                        className={
                            showCreateButton
                                ? ADD_BUTTON_HEADER_CLASS
                                : 'pointer-events-none invisible shrink-0'
                        }
                        aria-hidden={!showCreateButton}
                    >
                        <AddPublicationButton
                            compact
                            excludeFromTabOrder
                            presetSchedule={presetSchedule}
                        />
                    </div>
                ) : null}
            </div>
            <div>
                {isSinglePublication ? (
                    <InlinePublicationCard
                        publication={publications[0]}
                        index={0}
                        variant={CalendarGranularity.Month}
                    />
                ) : (
                    visible.map((publication, index) => (
                        <PublicationPill
                            key={publication.id}
                            publication={publication}
                            index={index}
                            onHoverStart={onPublicationHoverStart}
                            onHoverEnd={onPublicationHoverEnd}
                        />
                    ))
                )}
                {extra > 0 && (
                    <MoreCellPublicationsButton
                        day={day}
                        extra={extra}
                        onOpenDayList={onOpenDayList}
                    />
                )}
            </div>
            {showCreateButton && !hasPublications && (
                <div className={ADD_BUTTON_OVERLAY_EMPTY_CLASS}>
                    <AddPublicationButton
                        compact
                        excludeFromTabOrder
                        presetSchedule={presetSchedule}
                    />
                </div>
            )}
        </div>
    );
}
